import type { ReactNode } from 'react'
import { useNls } from '../../../nls/nlsProvider'
import { renderInlineMarkdown } from '../../../utils/inlineMarkdown'
import type { HealthResult, ViewerTier } from '../../../utils/overviewInsights'
import { OVERVIEW_V2_LIMITS } from '../../../types/overview'
import { Textarea } from '../../Common/Textarea'

export interface HeaderSectionProps {
  workspaceName?: string
  brief: string
  health: HealthResult
  canActOnCount: number
  tier: ViewerTier
  goalProgressPercent: number
  isEditing?: boolean
  onBriefChange?: (brief: string) => void
  toolsNode?: ReactNode
}

export const HeaderSection = ({
  workspaceName,
  brief,
  health,
  canActOnCount,
  tier,
  goalProgressPercent,
  isEditing = false,
  onBriefChange,
  toolsNode,
}: HeaderSectionProps) => {
  const { t } = useNls()

  const isHealthBad = health.state === 'off_track'
  const isHealthWarn = health.state === 'at_risk'
  const healthPillClass = isHealthBad
    ? 'overviewPill overviewPill--bad'
    : isHealthWarn
      ? 'overviewPill overviewPill--warn'
      : 'overviewPill overviewPill--good'

  const healthText = health.worstCause
    ? `● ${health.label}: ${health.worstCause}`
    : `● ${health.label}`

  const roundedGoalPercent = Math.max(0, Math.min(100, Math.round(goalProgressPercent)))

  return (
    <div className="overviewCard overviewHero">
      {toolsNode}
      <div>
        {workspaceName && <span className="overviewHero__eyebrow">{workspaceName}</span>}
        <h2 className="overviewHero__heading">{t('NLSOverviewTitle')}</h2>

        {isEditing ? (
          <div>
            <Textarea
              className="overviewHero__briefTextarea"
              value={brief}
              maxLength={OVERVIEW_V2_LIMITS.MAX_TEXT_CHARS}
              placeholder={t('NLSOverviewBriefPlaceholder')}
              aria-label={t('NLSOverviewEditBrief')}
              onChange={(val) => onBriefChange?.(val)}
            />
          </div>
        ) : (
          <div
            className="overviewHero__brief"
            dangerouslySetInnerHTML={{ __html: renderInlineMarkdown(brief) }}
          />
        )}

        <div className="overviewHero__pills">
          <span className={healthPillClass}>{healthText}</span>
          {tier === 'lead' && canActOnCount > 0 && (
            <span className="overviewPill overviewPill--bad">
              {t('NLSOverviewNeedYou', { count: canActOnCount })}
            </span>
          )}
        </div>
      </div>

      <div
        className="overviewRing"
        style={{
          background: `conic-gradient(var(--accent) ${roundedGoalPercent}%, var(--surface-hover) 0)`,
        }}
        aria-label={`${roundedGoalPercent}% ${t('NLSOverviewGoalProgressRing')}`}
      >
        <div className="overviewRing__inner">
          <strong className="overviewRing__pct">{roundedGoalPercent}%</strong>
          <span className="overviewRing__caption">{t('NLSOverviewGoalProgressRing')}</span>
        </div>
      </div>
    </div>
  )
}
